import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  ListTodo, Phone, Plus, Loader2, Trash2, Pencil, X, PhoneCall,
  Mail, Building2, StickyNote, Clock, CheckCircle2, Circle, CircleDot,
  AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// The manager's business line on Quo. Calls placed from a device that has the
// Quo app set as its default dialer will go out from this number.
export const QUO_NUMBER = '1-877-239-9060';

const staffApi = async (path, { method = 'GET', token, body } = {}) => {
  const headers = { Authorization: `Bearer ${token}` };
  if (body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}/api/timetrack${path}`, {
    method, headers, body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Failed (${res.status})`); }
  return res.status === 204 ? null : res.json();
};

const telHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

// Client-side due-date bucketing (mirrors backend reminder logic).
export const taskDueBucket = (dueDateStr) => {
  if (!dueDateStr) return null;
  const due = new Date(`${dueDateStr}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((due - today) / 86400000);
  if (diffDays < 0) return 'overdue';
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  return null;
};

const DUE_BADGE = {
  overdue: 'bg-rose-100 text-rose-700 border-rose-200',
  today: 'bg-amber-100 text-amber-800 border-amber-200',
  tomorrow: 'bg-blue-100 text-blue-700 border-blue-200',
};
const DUE_LABEL = { overdue: 'Overdue', today: 'Due today', tomorrow: 'Due tomorrow' };

const PRIORITY_STYLES = {
  high: 'bg-rose-100 text-rose-700 border-rose-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STATUS_META = {
  todo: { label: 'To-do', icon: Circle, cls: 'text-gray-500' },
  in_progress: { label: 'In progress', icon: CircleDot, cls: 'text-blue-600' },
  done: { label: 'Done', icon: CheckCircle2, cls: 'text-emerald-600' },
};

/* ============================= TASKS ============================= */
export const VATasksPanel = ({ token }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [noteDraft, setNoteDraft] = useState({}); // id -> note text
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await staffApi('/my-tasks', { token });
      setTasks(d);
      setNoteDraft(Object.fromEntries(d.map((t) => [t.id, t.notes || ''])));
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (task, status) => {
    setSavingId(task.id); setErr('');
    try {
      const updated = await staffApi(`/my-tasks/${task.id}`, { method: 'PATCH', token, body: { status } });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
    } catch (e) { setErr(e.message); } finally { setSavingId(null); }
  };

  const saveNote = async (task) => {
    setSavingId(task.id); setErr('');
    try {
      const updated = await staffApi(`/my-tasks/${task.id}`, { method: 'PATCH', token, body: { notes: noteDraft[task.id] ?? '' } });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
    } catch (e) { setErr(e.message); } finally { setSavingId(null); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>;

  const openTasks = tasks.filter((t) => t.status !== 'done');
  const dueBuckets = { overdue: [], today: [], tomorrow: [] };
  openTasks.forEach((t) => { const b = taskDueBucket(t.due_date); if (b) dueBuckets[b].push(t); });
  const nudgeCount = dueBuckets.overdue.length + dueBuckets.today.length;

  return (
    <div className="space-y-3" data-testid="va-tasks-panel">
      <div className="flex items-center gap-2">
        <ListTodo className="h-5 w-5 text-[#0A2A43]" />
        <h2 className="font-bold text-[#0A2A43]">My Tasks</h2>
        <span className="text-xs text-gray-400">{tasks.length} assigned</span>
      </div>
      {nudgeCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-800" data-testid="va-tasks-nudge">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            You have{' '}
            {dueBuckets.overdue.length > 0 && <b>{dueBuckets.overdue.length} overdue</b>}
            {dueBuckets.overdue.length > 0 && dueBuckets.today.length > 0 && ' and '}
            {dueBuckets.today.length > 0 && <b>{dueBuckets.today.length} due today</b>}
            {' '}task{nudgeCount > 1 ? 's' : ''}. Please update their status once done.
          </span>
        </div>
      )}
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">{err}</div>}
      {tasks.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500 text-sm shadow-sm" data-testid="va-tasks-empty">
          No tasks assigned yet. Your manager will add tasks here.
        </div>
      )}
      {tasks.map((t) => {
        const meta = STATUS_META[t.status] || STATUS_META.todo;
        const StatusIcon = meta.icon;
        const dueB = t.status !== 'done' ? taskDueBucket(t.due_date) : null;
        return (
          <div key={t.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`va-task-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide font-medium ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.medium}`}>{t.priority}</span>
                  {dueB && <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${DUE_BADGE[dueB]}`} data-testid={`va-task-due-badge-${t.id}`}>{DUE_LABEL[dueB]}</span>}
                  <h3 className="font-semibold text-[#0A2A43] truncate">{t.title}</h3>
                </div>
                {t.description && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{t.description}</p>}
                {t.due_date && <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Due {t.due_date}</p>}
              </div>
              <span className={`flex items-center gap-1 text-xs font-medium ${meta.cls}`}><StatusIcon className="h-4 w-4" /> {meta.label}</span>
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-gray-400">Update status:</span>
              {Object.entries(STATUS_META).map(([key, m]) => (
                <button key={key} onClick={() => updateStatus(t, key)} disabled={savingId === t.id || t.status === key}
                  data-testid={`va-task-status-${t.id}-${key}`}
                  className={`text-xs px-3 py-1 rounded-full border transition-all ${t.status === key ? 'bg-[#0A2A43] text-white border-[#0A2A43]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#0A2A43]'}`}>
                  {m.label}
                </button>
              ))}
            </div>

            <div className="mt-3">
              <label className="text-[11px] font-medium text-gray-500 flex items-center gap-1 mb-1"><StickyNote className="h-3 w-3" /> Notes</label>
              <textarea value={noteDraft[t.id] ?? ''} onChange={(e) => setNoteDraft((p) => ({ ...p, [t.id]: e.target.value }))}
                data-testid={`va-task-notes-${t.id}`} rows={2} placeholder="Add a note about your progress…"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]" />
              <div className="flex justify-end mt-1">
                <Button size="sm" variant="outline" onClick={() => saveNote(t)} disabled={savingId === t.id} data-testid={`va-task-save-note-${t.id}`}>
                  {savingId === t.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Save note
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ============================= CALL LIST ============================= */
const emptyContact = { first_name: '', last_name: '', cell_phone: '', main_phone: '', email: '', company: '', address: '', city: '', postal_code: '', notes: '' };

export const VACallListPanel = ({ token }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyContact);
  const [saving, setSaving] = useState(false);
  const [logFor, setLogFor] = useState(null); // contact object
  const [logForm, setLogForm] = useState({ outcome: '', note: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { setContacts(await staffApi('/my-contacts', { token })); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyContact); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    const parts = (c.name || '').trim().split(' ');
    setForm({
      first_name: c.first_name || parts.slice(0, -1).join(' ') || parts[0] || '',
      last_name: c.last_name || (parts.length > 1 ? parts.slice(-1).join(' ') : ''),
      cell_phone: c.cell_phone || c.phone || '',
      main_phone: c.main_phone || '',
      email: c.email || '', company: c.company || '',
      address: c.address || '', city: c.city || '', postal_code: c.postal_code || '',
      notes: c.notes || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      if (editing) {
        const upd = await staffApi(`/my-contacts/${editing.id}`, { method: 'PATCH', token, body: form });
        setContacts((prev) => prev.map((c) => (c.id === editing.id ? upd : c)));
      } else {
        const created = await staffApi('/my-contacts', { method: 'POST', token, body: form });
        setContacts((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setShowForm(false);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name} from your call list?`)) return;
    try { await staffApi(`/my-contacts/${c.id}`, { method: 'DELETE', token }); setContacts((prev) => prev.filter((x) => x.id !== c.id)); }
    catch (e) { setErr(e.message); }
  };

  const saveLog = async () => {
    setSaving(true); setErr('');
    try {
      const upd = await staffApi(`/my-contacts/${logFor.id}/log`, { method: 'POST', token, body: logForm });
      setContacts((prev) => prev.map((c) => (c.id === logFor.id ? upd : c)));
      setLogFor(null); setLogForm({ outcome: '', note: '' });
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>;

  return (
    <div className="space-y-3" data-testid="va-calllist-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Phone className="h-5 w-5 text-[#0A2A43]" />
          <h2 className="font-bold text-[#0A2A43]">Call List</h2>
          <span className="text-xs text-gray-400">{contacts.length} contacts</span>
        </div>
        <Button size="sm" onClick={openAdd} className="bg-[#0A2A43] text-white" data-testid="va-add-contact-btn"><Plus className="h-4 w-4 mr-1" /> Add contact</Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2" data-testid="va-quo-banner">
        <PhoneCall className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>Tap <b>Call</b> to dial. Calls placed on a device with the <b>Quo app set as the default dialer</b> go out from the business number <b>{QUO_NUMBER}</b>.</span>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">{err}</div>}

      {contacts.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500 text-sm shadow-sm" data-testid="va-contacts-empty">
          No contacts yet. Add someone to call, or your manager can add them for you.
        </div>
      )}

      {contacts.map((c) => (
        <div key={c.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`va-contact-${c.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-[#0A2A43]">{c.name} {c.created_by === 'admin' && <span className="text-[10px] text-gray-400 font-normal">(assigned)</span>}</p>
              <p className="text-sm text-gray-600 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.cell_phone || c.phone}{c.cell_phone ? ' (cell)' : ''}</p>
              {c.main_phone && c.main_phone !== (c.cell_phone || c.phone) && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" /> {c.main_phone} (main)</p>}
              {(c.address || c.city || c.postal_code) && <p className="text-xs text-gray-500">{[c.address, c.city, c.postal_code].filter(Boolean).join(', ')}</p>}
              {c.company && <p className="text-xs text-gray-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> {c.company}</p>}
              {c.email && <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</p>}
              {c.notes && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{c.notes}</p>}
            </div>
            <div className="flex items-center gap-1">
              <a href={telHref(c.cell_phone || c.phone)} data-testid={`va-call-${c.id}`}
                className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                <PhoneCall className="h-4 w-4" /> Call
              </a>
              <Button size="sm" variant="outline" onClick={() => { setLogFor(c); setLogForm({ outcome: '', note: '' }); }} data-testid={`va-log-${c.id}`}>Log</Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`va-edit-contact-${c.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => remove(c)} data-testid={`va-delete-contact-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
          {(c.call_logs || []).length > 0 && (
            <div className="mt-3 border-t pt-2 space-y-1" data-testid={`va-contact-logs-${c.id}`}>
              {(c.call_logs || []).slice(0, 3).map((lg) => (
                <p key={lg.id} className="text-[11px] text-gray-500">
                  <span className="font-medium text-gray-700">{lg.outcome}</span>{lg.note ? ` — ${lg.note}` : ''} · {new Date(lg.at).toLocaleString()}
                </p>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Add / Edit contact modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="va-contact-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-[#0A2A43]">{editing ? 'Edit contact' : 'Add contact'}</h3><button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="First Name *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-first-name" />
                <input placeholder="Last Name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-last-name" />
                <input placeholder="Cell Phone *" value={form.cell_phone} onChange={(e) => setForm({ ...form, cell_phone: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-cell-phone" />
                <input placeholder="Main Phone" value={form.main_phone} onChange={(e) => setForm({ ...form, main_phone: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-main-phone" />
                <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-email" />
                <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-company" />
                <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="px-3 py-2 border rounded-lg text-sm col-span-2" data-testid="va-contact-address" />
                <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-city" />
                <input placeholder="Postal Code" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-postal" />
              </div>
              <textarea placeholder="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="va-contact-notes" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#0A2A43] text-white" data-testid="va-contact-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} {editing ? 'Save' : 'Add'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Call log modal */}
      {logFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setLogFor(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="va-log-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-[#0A2A43]">Log call — {logFor.name}</h3><button onClick={() => setLogFor(null)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              <select value={logForm.outcome} onChange={(e) => setLogForm({ ...logForm, outcome: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="va-log-outcome">
                <option value="">Select outcome…</option>
                <option>Reached — spoke with contact</option>
                <option>Left voicemail</option>
                <option>No answer</option>
                <option>Busy / call back later</option>
                <option>Wrong number</option>
                <option>Not interested</option>
                <option>Follow-up scheduled</option>
              </select>
              <textarea placeholder="Note (optional)" rows={2} value={logForm.note} onChange={(e) => setLogForm({ ...logForm, note: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="va-log-note" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setLogFor(null)}>Cancel</Button>
              <Button onClick={saveLog} disabled={saving || !logForm.outcome} className="bg-[#0A2A43] text-white" data-testid="va-log-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save log</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
